import React, { ReactElement } from 'react';
import { Field, Fields } from 'redux-form/immutable';
import { Button, FormControl, Grid, InputLabel, MenuItem } from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';
import { List } from 'immutable';

import DialogButton from 'retired/DialogButton';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormSelect from 'retired/FormSelect';
import FormTextField from 'retired/FormTextField';
import BudgetAllocationType from 'entities/budgetAllocation/model';
import * as validators from 'utils/validators';
import { currencyFormat, currencies } from 'utils/currencyFormat';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormLocationSelector from 'retired/FormLocationSelector';
import Location from 'entities/location/model';
import { VOLUNTEER_REWARD_TYPE_NAME } from 'entities/budgetAllocation/constants';

const required = validators.required();

const CurrencyCodeSelector = ({ budget, ...rest }: { budget: any; rest: any }): ReactElement => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <FormSelect displayEmpty {...rest}>
    {[{ currencyCode: '', currency: { label: 'Select a currency' } }]
      .concat(
        Object.keys(currencies).map((currencyCode) => ({
          currencyCode,
          currency: currencies[currencyCode],
        }))
      )
      .map(({ currencyCode, currency }) => (
        <MenuItem
          key={currencyCode}
          value={currencyCode}
          disabled={budget?.currencyCode !== currencyCode}
        >
          {currency.label}
        </MenuItem>
      ))}
  </FormSelect>
);

const BudgetAllocation = ({
  budgetAllocation,
  budget,
  pristine,
  invalid,
  budgetAllocationId,
  remove,
  extraMatchingGrantsFields = false,
  budgetAllocationTypeName,
  suffixText,
  locations,
  requireStartAndEndDate = false,
}: {
  budgetAllocation: BudgetAllocationType;
  budget: any;
  pristine: boolean;
  invalid: boolean;
  remove: () => void;
  budgetAllocationId: string;
  extraMatchingGrantsFields: boolean;
  budgetAllocationTypeName: string;
  suffixText: string;
  locations: Iterable<string, Location>;
  requireStartAndEndDate: boolean;
}): ReactElement => {
  const selectedCurrencyCode =
    budgetAllocation?.get('currencyCode') || budget.currencyCode || 'USD';

  const selectedCurrency = currencies[selectedCurrencyCode];

  const spellOut = React.useMemo(() => {
    const selectedLimitTotal = Number(budgetAllocation?.get('limitTotal') || budget?.limitTotal);

    const initialAllocationSentence = `Allocated up to ${
      Number.isFinite(selectedLimitTotal)
        ? currencyFormat(selectedLimitTotal, selectedCurrencyCode)
        : "the parent budget's limit"
    } for ${budgetAllocationTypeName.toLowerCase()} to nonprofits${suffixText}.`;

    const locationsSentence =
      budgetAllocation?.get('locationsExclude') && budgetAllocation?.get('locations').size
        ? ' Excluding employee locations selected above'
        : budgetAllocation?.get('locations').size
        ? ' Only including employee locations selected above'
        : '';

    return `${initialAllocationSentence} ${locationsSentence}`;
  }, [budgetAllocation, budgetAllocationTypeName, suffixText, selectedCurrencyCode]);

  const validateTotalLimit = (value: string, passedBudgetAllocation: BudgetAllocationType) => {
    if (value !== '') {
      if (Number(value) <= 0) {
        return 'Spending limit must be greater than or equal to 0';
      }

      if (passedBudgetAllocation.get('totalIssued') > Number(value)) {
        return 'You can not set the spending limit to less than what has already been spent';
      }

      if (Number.isFinite(budget?.limitTotal) && budget?.limitTotal < Number(value)) {
        return 'You can not set the spending limit to more than the parent budget';
      }
    }

    return undefined;
  };

  const validateLimitPerUser = (value: string, allValues: any) => {
    if (value !== null) {
      const numericValue = Number(value);
      const providedLimitTotal = allValues.get('limitTotal');
      const numericLimitTotal = Number(providedLimitTotal);

      if (numericValue <= 0) {
        return 'Limit per user must be greater than or equal to 0';
      }

      // limitPerUser can NOT equal limitTotal since we asssume multiple users per company
      if (Number.isFinite(numericLimitTotal) && numericValue >= numericLimitTotal) {
        return 'You can not set the limit per user to greater than or equal to the total allocated';
      }
    }

    return undefined;
  };

  const validateRewardAmount = (value: string, allValues: any) => {
    if (value !== null) {
      const numericValue = Number(value);
      const providedLimitPerUser = allValues.get('limitPerUser');
      const numericLimitPerUser = Number(providedLimitPerUser);

      if (numericValue <= 0) {
        return 'Reward amount must be greater than or equal to 0';
      }

      // rewardAmount can equal limitPerUser since we assume each user can also be awarded just once
      if (Number.isFinite(numericLimitPerUser) && numericValue > numericLimitPerUser) {
        return 'You can not set the reward amount to greater than the limit per user';
      }
    }

    return undefined;
  };

  const validateLocations = (value: List<string>, passedBudgetAllocation: BudgetAllocationType) => {
    if (passedBudgetAllocation && !passedBudgetAllocation.get('locationsExclude') && !value?.size) {
      return 'The rule is set to only include certain locations and none are selected';
    }
    return undefined;
  };

  return (
    <div
      style={{
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 30,
        marginBottom: 30,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ flexGrow: 1 }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>{budgetAllocationTypeName} Budget Rule Name</InputLabel>
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <Field
              name="title"
              component={FormTextField}
              placeholder="Budget Rule Name"
              validate={required}
              required
            />
          </FormControl>
        </div>
        {budgetAllocationId !== 'new' && !budgetAllocation?.get('totalIssued') && (
          <DialogButton
            style={{ display: 'inline-block' }}
            title={`Remove ${budgetAllocationTypeName} Budget Rule`}
            text={`Are you sure you want to remove this ${budgetAllocationTypeName} Budget Rule?`}
            confirmLabel="Remove"
            cancelLabel="Cancel"
            iconButton
            handleConfirm={remove}
          >
            <DeleteIcon />
          </DialogButton>
        )}
      </div>
      <div style={{ marginBottom: 30 }}>
        <Grid container spacing={3}>
          <Grid item md={4}>
            <Field
              name="currencyCode"
              component={CurrencyCodeSelector}
              validate={required}
              required
              type="string"
              budget={budget}
              label="Budget Rule currency"
              labelProps={{
                shrink: true,
              }}
              formControlProps={{
                style: { width: '70%', height: 17 },
              }}
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Total allocated</InputLabel>
            </FormControl>
            {selectedCurrency?.symbol}
            <Field
              name="limitTotal"
              component={FormTextField}
              placeholder="Total allocated"
              max={Number.isFinite(budget?.limitTotal) ? budget.limitTotal : Infinity}
              min={0}
              type="number"
              validate={validateTotalLimit}
              alwaysDisplayErrors
            />
          </Grid>
          <Grid item md={4}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>
                Start &amp; End Date {!requireStartAndEndDate && '(Optional)'}
              </InputLabel>
            </FormControl>
            <div>
              <Fields
                names={[`startingAt`, `endingAt`]}
                component={FormDateWithStartAndEnd}
                future
                displayTimeAndTimeZone
                validate={requireStartAndEndDate ? required : undefined}
                required={requireStartAndEndDate}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      {extraMatchingGrantsFields && (
        <div style={{ marginBottom: 30 }}>
          <Grid container spacing={3}>
            <Grid item md>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Limit Per User</InputLabel>
              </FormControl>
              {selectedCurrency.symbol}
              <Field
                name="limitPerUser"
                component={FormTextField}
                validate={validateLimitPerUser}
                placeholder="Limit Per User"
                type="number"
              />
            </Grid>
            <Grid item md>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Reward Amount per Volunteering Hour</InputLabel>
              </FormControl>
              {selectedCurrency.symbol}
              <Field
                name="rewardAmount"
                component={FormTextField}
                validate={validateRewardAmount}
                placeholder="Reward Amount"
                type="number"
              />
            </Grid>
            <Grid item md>
              {/* We can add isAmountPerHour later here if needed */}
            </Grid>
          </Grid>
        </div>
      )}
      {budgetAllocationTypeName === VOLUNTEER_REWARD_TYPE_NAME && (
        <div style={{ marginBottom: 30 }}>
          <FormIncludeExcludeSelector
            name="locationsExclude"
            labelText="the following user locations:"
          />
          <Field
            name="locations"
            component={FormLocationSelector}
            props={{ locations }}
            validate={validateLocations}
          />
        </div>
      )}
      <div>
        Rule summary:&#20;
        {spellOut}
      </div>
      <div style={{ marginTop: 30 }}>
        <Button type="submit" disabled={invalid} color="primary" variant="contained">
          {pristine ? 'Close' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};

export default BudgetAllocation;
